import * as React from 'react';
// @ts-ignore
import ReactCrop  = require('react-image-crop');
import {
  cropImageLocally, rotateImageLocally, DataURLFromFile,
  ErrCouldNotCompressFile
} from '../../utils/image';
import Button from 'reactstrap/lib/Button';
import { LProps, withLocalization } from '../../utils/wrappers';
import { compose, Dispatch, bindActionCreators } from 'redux';
import 'react-image-crop/dist/ReactCrop.css';
import './imageEditor.scss';
import actions from '../../redux/actions';
import { connect } from 'react-redux';
import { State } from '../../redux/state';
import { UserRead } from '../../userApi';
import { uuid } from '../../utils/uuid';
import { StyleState } from '../../utils/styles';

interface ManualProps {
  dataURLFromFile: DataURLFromFile;
  onChange: (dataURLFromFile: DataURLFromFile) => void;
  onCropApplied?: () => void;
}

interface Props extends ManualProps {
  user: UserRead;
  showToast: (message: string) => void;
}

interface ImageEditorState {
  processedDataURL: DataURLFromFile;
  /** Percent crop is required by this package's legacy render component. */
  crop: ReactCrop.Crop;
  /** Pixel crop comes directly from onChange and is used for canvas output. */
  pixelCrop: ReactCrop.Crop;
  image: HTMLImageElement;
  busy: boolean;
}

const initialState: ImageEditorState = {
  processedDataURL: null,
  crop: {
    unit: '%',
    height: 0,
    width: 0,
    x: 0,
    y: 0
  },
  pixelCrop: {
    unit: 'px',
    height: 0,
    width: 0,
    x: 0,
    y: 0
  },
  image: null,
  busy: false
};

const centeredCrops = (image: HTMLImageElement) => {
  const width = image.clientWidth || image.width;
  const height = image.clientHeight || image.height;
  return {
    percent: {
      unit: '%',
      x: 5,
      y: 5,
      width: 90,
      height: 90
    } as ReactCrop.Crop,
    pixel: {
      unit: 'px',
      x: width * 0.05,
      y: height * 0.05,
      width: width * 0.9,
      height: height * 0.9
    } as ReactCrop.Crop
  };
};

/** Map ReactCrop's displayed pixel selection onto the source bitmap. */
const displayCropToSourcePixels = (
  pixelCrop: ReactCrop.Crop,
  image: HTMLImageElement
): { x: number; y: number; width: number; height: number } | null => {
  const naturalW = image.naturalWidth;
  const naturalH = image.naturalHeight;
  const displayW = image.clientWidth || image.width || naturalW;
  const displayH = image.clientHeight || image.height || naturalH;
  if (!naturalW || !naturalH || !displayW || !displayH ||
      !pixelCrop || !(pixelCrop.width > 0) || !(pixelCrop.height > 0)) {
    return null;
  }

  const x = Math.max(0, Math.round(pixelCrop.x * (naturalW / displayW)));
  const y = Math.max(0, Math.round(pixelCrop.y * (naturalH / displayH)));
  const width = Math.max(1, Math.min(
    Math.round(pixelCrop.width * (naturalW / displayW)), naturalW - x
  ));
  const height = Math.max(1, Math.min(
    Math.round(pixelCrop.height * (naturalH / displayH)), naturalH - y
  ));

  if (width < naturalW * 0.05 || height < naturalH * 0.05) {
    return null;
  }
  return { x, y, width, height };
};

class ImageEditor extends React.Component<LProps<Props>, ImageEditorState> {
  public state = { ...initialState };
  private id = uuid();
  private cropElement: any;

  public async componentDidMount() {
    this.initializeDataURLFromFile(this.props.dataURLFromFile);
  }

  public async componentWillReceiveProps(nextProps: LProps<Props>) {
    const prevFile = this.props.dataURLFromFile;
    const nextFile = nextProps.dataURLFromFile;
    if (prevFile === nextFile) {
      return;
    }
    const prevUrl = prevFile && prevFile.dataURL;
    const nextUrl = nextFile && nextFile.dataURL;
    if (prevUrl && nextUrl && prevUrl === nextUrl) {
      return;
    }
    this.initializeDataURLFromFile(nextFile);
  }

  private async withBusy<T>(work: () => Promise<T>): Promise<T | undefined> {
    const { showToast, t } = this.props;
    if (this.state.busy) return;
    this.setState({ busy: true });
    try {
      return await work();
    } catch (err) {
      const isTooLarge = err === ErrCouldNotCompressFile ||
        (err && err.message && String(err.message).toLowerCase().indexOf('compress') >= 0);
      showToast(
        isTooLarge
          ? t('This photo is too large to upload. Try a smaller image.')
          : ((err && err.message) || t('We had trouble editing your photo. Try cropping this photo or using another.'))
      );
      return;
    } finally {
      this.setState({ busy: false });
    }
  }

  private async rotateBy(deltaDegrees: number) {
    const { onChange, dataURLFromFile, showToast, t } = this.props;
    if (!dataURLFromFile || !dataURLFromFile.dataURL) {
      showToast(t('We had trouble editing your photo. Try cropping this photo or using another.'));
      return;
    }
    await this.withBusy(async () => {
      const rotated = await rotateImageLocally(dataURLFromFile, deltaDegrees);
      if (!rotated || !rotated.dataURL) {
        throw new Error(t('We had trouble editing your photo. Try cropping this photo or using another.'));
      }
      onChange(rotated);
    });
  }

  private async cutCrop() {
    const { pixelCrop, image } = this.state;
    const { onCropApplied, onChange, dataURLFromFile, showToast, t } = this.props;
    const imageEl = image || (this.cropElement && this.cropElement.imageRef);
    if (!imageEl || !pixelCrop || !pixelCrop.width || !pixelCrop.height) {
      showToast(t('Crop area is too small. Drag the corners to select more of the photo.'));
      return;
    }
    const naturalCrop = displayCropToSourcePixels(pixelCrop, imageEl);
    if (!naturalCrop) {
      showToast(t('Crop area is too small. Drag the corners to select more of the photo.'));
      return;
    }
    await this.withBusy(async () => {
      const cropped = await cropImageLocally(dataURLFromFile, naturalCrop);
      if (!cropped || !cropped.dataURL) {
        throw new Error(t('We had trouble editing your photo. Try cropping this photo or using another.'));
      }
      onChange(cropped);
      if (onCropApplied) {
        onCropApplied();
      }
    });
  }

  private useFullPhoto() {
    const { onCropApplied } = this.props;
    if (onCropApplied) {
      onCropApplied();
    }
  }

  /**
   * The installed package declares v8, but its public Component export is an
   * older build whose callback order is (percentCrop, pixelCrop), the reverse
   * of current react-image-crop. Mixing these up turns e.g. 497px into 497%,
   * which makes the selection disappear as soon as it is dragged.
   */
  private onCropChange = (percentCrop: ReactCrop.Crop, pixelCrop: ReactCrop.Crop) => {
    if (!pixelCrop || !percentCrop) return;
    const pixelValues = [pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height];
    const percentValues = [percentCrop.x, percentCrop.y, percentCrop.width, percentCrop.height];
    if (!pixelValues.concat(percentValues).every((n) => typeof n === 'number' && isFinite(n))) return;
    if (!(pixelCrop.width > 0) || !(pixelCrop.height > 0) ||
        !(percentCrop.width > 0) || !(percentCrop.height > 0)) return;
    if (percentCrop.x < 0 || percentCrop.y < 0 ||
        percentCrop.x + percentCrop.width > 100.5 ||
        percentCrop.y + percentCrop.height > 100.5) return;
    this.setState({
      pixelCrop: { ...pixelCrop, unit: 'px' },
      crop: { ...percentCrop, unit: '%' }
    });
  }

  private onImageLoaded = (img: HTMLImageElement) => {
    const apply = () => {
      const centered = centeredCrops(img);
      this.setState({ image: img, crop: centered.percent, pixelCrop: centered.pixel });
    };
    // Wait for the rendered bitmap dimensions before creating the pixel crop.
    if (img.clientWidth > 0 && img.clientHeight > 0) {
      apply();
    } else {
      requestAnimationFrame(() => requestAnimationFrame(apply));
    }
    // Returning false prevents ReactCrop from overwriting our crop on load.
    return false;
  }

  private initializeDataURLFromFile(dataURLFromFile: DataURLFromFile) {
    this.setState({
      processedDataURL: dataURLFromFile,
      crop: initialState.crop,
      pixelCrop: initialState.pixelCrop,
      image: null,
      busy: false
    });
  }

  public render() {
    const { processedDataURL, crop, busy } = this.state;
    const { t } = this.props;
    const hasCrop = Boolean(crop && crop.width > 0 && crop.height > 0);
    const canApplyCrop = Boolean(processedDataURL && hasCrop) && !busy;
    return (
      <div id={this.id} className="cc__image_editor">
        {(processedDataURL) &&
          <div className="cc__react_crop__container">
            {/* @ts-ignore*/}
            <ReactCrop.Component
              ref={(ref: React.Ref<ReactCrop>) => { this.cropElement = ref; }}
              onImageLoaded={this.onImageLoaded}
              src={processedDataURL.dataURL}
              crop={hasCrop ? crop : undefined}
              keepSelection={true}
              minWidth={20}
              minHeight={20}
              onChange={this.onCropChange}
              onComplete={this.onCropChange}
              className="cc__react_crop"
              imageStyle={{
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
        }
        <p className="cc__image_editor__help">
          {t('Drag the corner handles to resize. Drag inside the box to move it. Then tap Apply crop, or Use full photo to skip.')}
        </p>
        <div className="cc__image_editor__controls">
          <Button
            outline={true}
            size="sm"
            aria-label={t('Rotate left')}
            color="primary"
            disabled={busy}
            onClick={() => this.rotateBy(-90)}
          ><i className="fa fa-rotate-left" /></Button>
          <Button
            outline={true}
            size="sm"
            color="primary"
            aria-label={t('Rotate right')}
            disabled={busy}
            onClick={() => this.rotateBy(90)}
          ><i className="fa fa-rotate-right" /></Button>
          <Button
            outline={true}
            size="sm"
            aria-label={t('Use full photo')}
            color="primary"
            disabled={busy}
            onClick={() => this.useFullPhoto()}
          >{t('Use full photo')}</Button>
          <Button
            size="sm"
            aria-label={t('Apply crop')}
            color="primary"
            disabled={!canApplyCrop}
            onClick={() => this.cutCrop()}
          ><i className="fa fa-check" aria-hidden={true} /> {t('Apply crop')}</Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State) => ({
  user: state.user
});
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  showToast: (message: string) => actions.toast({ style: StyleState.Warning, message }, 5000)
}, dispatch);

export default compose(
  withLocalization('imageEditor'),
  connect(mapStateToProps, mapDispatchToProps)
)(ImageEditor) as React.SFC<ManualProps>;
